import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TickCircle } from "iconsax-reactjs";
import CTAs from "../components/CTAs";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(3);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const sessionId = searchParams.get("session_id");

        if (!sessionId) {
          throw new Error("No session ID found");
        }

        const verifyResponse = await fetch(
          `${API_URL}/api/subscription/verify-session?session_id=${sessionId}`
        );

        if (!verifyResponse.ok) {
          const errData = await verifyResponse.json();
          throw new Error(errData.message || "Payment verification failed");
        }

        // No need to store token/user — they will login fresh
        setLoading(false);

        // Start countdown then navigate to login
        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              navigate("/login");
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(interval);
      } catch (err) {
        console.error("Error verifying payment:", err);
        setError(err.message || "Failed to verify payment");
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate, API_URL]);

  const handleContinue = () => {
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-neutrals-neutral-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-primary-primary-100 border-t-primary-primary-500 mb-6"></div>
          <h2 className="text-2xl font-bold text-neutrals-neutral-900 mb-2">
            Verifying Payment
          </h2>
          <p className="text-neutrals-neutral-600">
            Please wait while we confirm your subscription...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen bg-neutrals-neutral-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-neutrals-neutral-900 mb-2">
            Payment Verification Failed
          </h2>
          <p className="text-neutrals-neutral-600 mb-6">{error}</p>
          <div className="space-y-3">
            <CTAs
              type="Gradientbtn"
              text="Contact Support"
              width="w-full"
              onClick={() =>
                (window.location.href = "mailto:help@peachlovesdigital.co.uk")
              }
            />
            <CTAs
              type="Secondarybtn"
              text="Go to Login"
              width="w-full"
              onClick={() => navigate("/login")}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-neutrals-neutral-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <TickCircle size={40} color="#10B981" variant="Bold" />
        </div>
        <h2 className="text-2xl font-bold text-neutrals-neutral-900 mb-2">
          Payment Successful! 🎉
        </h2>
        <p className="text-neutrals-neutral-600 mb-6">
          Your subscription is now active. Redirecting you to login in{" "}
          {countdown} seconds...
        </p>
        <CTAs
          type="Gradientbtn"
          text="Go to Login"
          width="w-full"
          onClick={handleContinue}
        />
        <p className="text-xs text-neutrals-neutral-400 mt-4">
          You can now invite team members and start creating LBAs
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;